import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { WizardStepper } from '../../components/layout/WizardStepper';
import { AppNav } from '../../components/layout/AppNav';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { FeedbackModal } from '../../components/ui/FeedbackModal';
import { useWizardStore } from '../../store/wizard.store';
import { offersService, type Offer } from '../../services/offers.service';
import { formatSAR, formatDate } from '../../utils/formatters';
import { useToast } from '../../components/ui/Toast';
import { MapPin, CheckCircle2, AlertCircle, FileText } from 'lucide-react';

const WIZARD_STEPS = [
  { label: 'Product', path: '/catalog' },
  { label: 'Location & Coverage', path: '/offers/wizard/location' },
  { label: 'Cart', path: '/offers/wizard/cart' },
  { label: 'Customer', path: '/offers/wizard/customer' },
  { label: 'Auth. Person', path: '/offers/wizard/authorized-person' },
  { label: 'Summary', path: '/offers/wizard/summary' },
];

const DOC_LABELS: Record<string, string> = {
  customerCR: 'Commercial Registration',
  customerVAT: 'VAT Certificate',
  nationalAddress: 'National Address Document',
  authorizedPersonLetter: 'Authorized Person Letter',
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="card">
    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)' }}>
      <span className="section-title" style={{ marginBottom: 0 }}>{title}</span>
    </div>
    <div className="card-body">{children}</div>
  </div>
);

const DetailGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>{children}</div>
);

const DetailRow: React.FC<{ label: string; value?: string | null; fullWidth?: boolean }> = ({ label, value, fullWidth }) => (
  <div style={fullWidth ? { gridColumn: '1 / -1' } : {}}>
    <div style={{ fontSize: '11px', color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '2px', fontWeight: 600 }}>{label}</div>
    <div style={{ fontSize: '13.5px', fontWeight: 500, color: 'var(--text-1)' }}>{value || '—'}</div>
  </div>
);

export const OrderSummaryStep: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { offerId, reset, documents } = useWizardStore();
  const [offer, setOffer] = useState<Offer | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    if (!offerId) return;
    offersService.get(offerId).then(setOffer).catch(() => showToast('Failed to load offer', 'error')).finally(() => setLoading(false));
  }, [offerId, showToast]);

  const handleGenerateOffer = async () => {
    if (!offerId) return;
    setGenerating(true);
    try {
      const channel = offer?.customer?.contactMobile ? 'sms' : 'email';
      const contact = offer?.customer?.contactMobile ?? offer?.customer?.contactEmail ?? '';
      const { url } = await offersService.generateLink(offerId, contact ? { channel, contact } : undefined);
      // Upload documents if any are attached (best-effort, non-blocking)
      const hasAnyDoc = Object.values(documents).some(Boolean);
      if (hasAnyDoc) {
        offersService.uploadDocuments(offerId, documents).catch(() => undefined);
      }
      setGeneratedLink(url);
      setShowFeedback(true);
    } catch {
      showToast('Failed to generate offer', 'error');
    } finally {
      setGenerating(false);
    }
  };

  if (!offerId) { navigate('/catalog'); return null; }

  if (loading) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <AppNav title="Order Summary" />
        <WizardStepper steps={WIZARD_STEPS} currentStep={5} />
        <main style={{ flex: 1, padding: '24px' }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="card" style={{ padding: '20px', marginBottom: '16px' }}>
              <div className="skeleton" style={{ height: '14px', width: '40%', marginBottom: '12px' }} />
              <div className="skeleton" style={{ height: '60px' }} />
            </div>
          ))}
        </main>
      </div>
    );
  }

  if (!offer) return null;

  const subtotal = offer.subtotal ?? 0;
  const vatAmount = offer.vatAmount ?? 0;
  const total = offer.total ?? 0;

  const missingItems: string[] = [];
  if (!offer.items?.length) missingItems.push('At least one product in cart');
  if (!offer.customer?.nameEn) missingItems.push('Customer name');
  if (!offer.customer?.contactMobile && !offer.customer?.contactEmail) missingItems.push('Customer contact (mobile or email)');
  if (!offer.authorizedPerson?.nameEn) missingItems.push('Authorized person name');
  if (!offer.authorizedPerson?.idNumber) missingItems.push('Authorized person ID number');
  const canGenerate = missingItems.length === 0;

  const docEntries = Object.entries(documents) as [keyof typeof documents, File | null][];

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppNav title="Order Summary" subtitle="Review everything before generating the offer" />
      <WizardStepper steps={WIZARD_STEPS} currentStep={5} />
      <main style={{ flex: 1, padding: '24px', overflowY: 'auto' }}>
        <div style={{ maxWidth: '1100px', display: 'flex', gap: '24px', alignItems: 'flex-start' }}>

          {/* LEFT — detail sections */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px' }}>

            {/* Products */}
            <Section title="Your Selection">
              {!(offer.items ?? []).length ? (
                <p style={{ color: 'var(--text-2)', fontSize: '13.5px' }}>No items.</p>
              ) : (
                (offer.items ?? []).map((item, idx) => (
                  <div key={item.id} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: idx > 0 ? '14px 0 0' : '0', borderTop: idx > 0 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: 'var(--p-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <FileText size={18} color="var(--p)" />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontWeight: 700, fontSize: '13.5px', color: 'var(--text-1)' }}>{item.product?.name ?? 'Product'}</span>
                        <Badge label={item.status} />
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' }}>{item.product?.category} · Monthly plan</div>
                      {item.location && (
                        <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <MapPin size={11} color="var(--p)" />
                          {item.location.addressLine1 ? `${item.location.addressLine1}${item.location.city ? `, ${item.location.city}` : ''}` : `${Number(item.location.lat).toFixed(4)}, ${Number(item.location.lng).toFixed(4)}`}
                        </div>
                      )}
                    </div>
                    <div style={{ fontWeight: 700, fontSize: '14px', flexShrink: 0, color: 'var(--text-1)' }}>
                      {formatSAR(item.price)}<span style={{ fontWeight: 400, fontSize: '12px', color: 'var(--text-3)' }}>/mo</span>
                    </div>
                  </div>
                ))
              )}
            </Section>

            {/* Contact */}
            {offer.customer && (
              <Section title="Contact Details">
                <DetailGrid>
                  <DetailRow label="Company (EN)" value={offer.customer.nameEn} />
                  <DetailRow label="Company (AR)" value={offer.customer.nameAr} />
                  {offer.customer.contactMobile && <DetailRow label="Mobile" value={offer.customer.contactMobile} />}
                  {offer.customer.contactEmail && <DetailRow label="Email" value={offer.customer.contactEmail} />}
                </DetailGrid>
              </Section>
            )}

            {/* Company */}
            {offer.customer && (
              <Section title="Company Details">
                <DetailGrid>
                  <DetailRow label="CR Number" value={offer.customer.crNumber} />
                  {offer.customer.crExpiry && <DetailRow label="CR Expiry" value={formatDate(offer.customer.crExpiry)} />}
                  {offer.customer.vatNumber && <DetailRow label="VAT Number" value={offer.customer.vatNumber} />}
                  {offer.customer.addressEn && <DetailRow label="Registered Address" value={offer.customer.addressEn} fullWidth />}
                </DetailGrid>
              </Section>
            )}

            {/* Authorized person */}
            {offer.authorizedPerson && (
              <Section title="Authorized Signatory">
                <DetailGrid>
                  <DetailRow label="Full Name" value={offer.authorizedPerson.nameEn} />
                  <DetailRow label="Mobile" value={offer.authorizedPerson.mobile} />
                  <DetailRow label="ID Type" value={offer.authorizedPerson.idType} />
                  <DetailRow label="ID Number" value={offer.authorizedPerson.idNumber} />
                </DetailGrid>
              </Section>
            )}

            {/* Documents checklist */}
            <Section title="Uploaded Documents">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {docEntries.map(([key, file]) => (
                  <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', borderRadius: '8px', background: file ? 'var(--succ-bg)' : 'var(--surface-alt)', border: `1px solid ${file ? '#BBF7D0' : 'var(--border)'}` }}>
                    {file
                      ? <CheckCircle2 size={15} color="var(--succ)" />
                      : <AlertCircle size={15} color="var(--text-3)" />
                    }
                    <span style={{ flex: 1, fontSize: '13px', fontWeight: 500, color: 'var(--text-1)' }}>{DOC_LABELS[key]}</span>
                    {file
                      ? <span style={{ fontSize: '12px', color: 'var(--succ)', fontWeight: 600 }}>{file.name}</span>
                      : <span style={{ fontSize: '12px', color: 'var(--text-3)' }}>Not uploaded</span>
                    }
                  </div>
                ))}
              </div>
            </Section>

            <div style={{ display: 'flex', gap: '8px' }}>
              <Button variant="ghost" onClick={() => navigate('/offers/wizard/authorized-person')}>Back</Button>
            </div>
          </div>

          {/* RIGHT — payment summary */}
          <div style={{ width: '300px', flexShrink: 0, position: 'sticky', top: '0', display: 'flex', flexDirection: 'column', gap: '14px' }}>
            <div className="card" style={{ overflow: 'hidden' }}>
              <div style={{ background: 'var(--p)', color: '#fff', padding: '14px 20px', fontSize: '13.5px', fontWeight: 700 }}>
                Monthly Payment
              </div>
              <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(offer.items ?? []).map((item) => (
                  <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
                    <span style={{ color: 'var(--text-2)', flex: 1, marginRight: '8px' }}>{item.product?.name ?? 'Product'}</span>
                    <span style={{ fontWeight: 600 }}>{formatSAR(item.price)}</span>
                  </div>
                ))}
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: '10px', marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text-2)' }}>
                    <span>Subtotal (excl. VAT)</span><span>{formatSAR(subtotal)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'var(--text-2)' }}>
                    <span>VAT included (15%)</span><span>{formatSAR(vatAmount)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: 800, color: 'var(--text-1)', borderTop: '2px solid var(--text-1)', paddingTop: '10px', marginTop: '4px' }}>
                    <span>Total</span>
                    <span style={{ color: 'var(--p)' }}>{formatSAR(total)}</span>
                  </div>
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', marginTop: '4px' }}>
                  Prices include applicable VAT
                </div>
              </div>
            </div>

            {!canGenerate && (
              <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: '8px', padding: '12px 14px', fontSize: '12.5px', color: 'var(--warn)' }}>
                <strong style={{ display: 'block', marginBottom: '6px' }}>Complete before sending:</strong>
                <ul style={{ margin: 0, paddingLeft: '16px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {missingItems.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}

            <Button
              onClick={handleGenerateOffer}
              loading={generating}
              disabled={!canGenerate}
              style={{ width: '100%', padding: '14px', fontSize: '14px', justifyContent: 'center' }}
            >
              Generate & Send Offer
            </Button>

            <div style={{ fontSize: '12px', color: 'var(--text-3)', textAlign: 'center', lineHeight: 1.5 }}>
              Offer #{offer.offerNumber} · Created {formatDate(offer.createdAt)}
            </div>
          </div>
        </div>
      </main>

      {offerId && (
        <FeedbackModal
          isOpen={showFeedback}
          offerId={offerId}
          offerNumber={offer?.offerNumber}
          link={generatedLink ?? undefined}
          contact={offer?.customer?.contactMobile ?? offer?.customer?.contactEmail ?? undefined}
          channel={offer?.customer?.contactMobile ? 'sms' : 'email'}
          authorizedPersonName={offer?.authorizedPerson?.nameEn ?? undefined}
          onSubmit={() => { setShowFeedback(false); reset(); navigate('/my-offers'); }}
        />
      )}
    </div>
  );
};
